import {
  AdminDollarInput2,
  AdminHeader,
  AdminInput,
  AdminSpinner,
  AdminSwitch,
  Box,
  Button,
  Disclaimer,
  Div,
  ErrorToast,
  H3,
  H5,
  P,
  PhoneNumberText,
  SuccessToast,
  VDriveLogo,
  VDrivePaymentMode,
  VDriveStatus,
  activateVDrive,
  getLocationVerificationVDriveWithId,
  lightTheme,
  queryKeys,
  rejectVDrive,
  reportError,
  useVDrive,
} from '@vgrubs/components';
import { useInvalidateQueries } from 'components/src/hooks/useInvalidateQueries';
import { LocationVDriveEntity } from 'components/src/types/location.types';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { OpenConversationButton } from '../Chat/OpenConversationButton';
import { ACH_CUSTOMER_ID_TOOLTIP } from '../VDrive/vDriveHelpers';
import { RejectVDriveModal } from './RejectVDriveModal';
import { KeyValueInfo } from './components/KeyValueInfo';
import { SectionCard } from './components/SectionCard';

/** The fees below these are refused on submit, so say so next to the fields. */
const MIN_VDRIVE_FEE_CENTS = 80;
const MIN_VGRUBS_FEE_CENTS = 20;

/** What the owner picked at activation; the top-up amounts are not the admin's concern here. */
const PAYMENT_MODE: Record<VDrivePaymentMode, { label: string; note: string }> =
  {
    [VDrivePaymentMode.perDelivery]: {
      label: 'Pay per delivery',
      note: 'No money up front. Each delivery is charged to the card.',
    },
    [VDrivePaymentMode.topUp]: {
      label: 'Prepaid balance (top-up)',
      note: 'The card tops up the balance up front.',
    },
  };

/**
 * One location asking for vDrive. Left: who they are. Right: what the admin sets before
 * enabling it: fees, ACH id, options. Same shape as RestaurantDetails.
 */
export const VDriveVerificationDetails = () => {
  const [loading, setLoading] = useState(false);
  const [vDriveVerification, setVDriveVerification] =
    useState<LocationVDriveEntity | null>(null);
  const [achCustomerId, setAchCustomerId] = useState('');
  const [vGrubsFee, setVGrubsFee] = useState<number | null>(25);
  const [vDriveFee, setVDriveFee] = useState<number | null>(200);
  const [enableVgrubsBrands, setEnableVgrubsBrands] = useState(false);
  const [autoAssignDriver, setAutoAssignDriver] = useState(false);
  const [rejectModal, setRejectModal] = useState(false);
  const navigate = useNavigate();
  const { setLocationVDrive, setBrandVDrive } = useVDrive();
  const { invalidateByKey } = useInvalidateQueries();
  const { id } = useParams() as { id: string };

  /** Either answer can change other locations' vDrive too, so drop everything cached. */
  function refreshAfterDecision() {
    invalidateByKey(queryKeys.admin.verifications.count);
    setLocationVDrive(null);
    setBrandVDrive(null);
    invalidateByKey(queryKeys.admin.verifications.vDrive);
    navigate(-1);
  }

  async function handleVerifyLocation() {
    if (Number(vDriveFee) < MIN_VDRIVE_FEE_CENTS) {
      ErrorToast(`vDrive Fee can't be less than $0.80`);
      return;
    }
    if (Number(vGrubsFee) < MIN_VGRUBS_FEE_CENTS) {
      ErrorToast(`vGrubs Fee can't be less than $0.20`);
      return;
    }

    const body = {
      enableVgrubsBrands,
      autoAssignDriver,
      achCustomerId,
      vDriveFee,
      vGrubsFee,
    };

    try {
      setLoading(true);
      const { data } = await activateVDrive(vDriveVerification!.id, body);
      SuccessToast(data.message);
      refreshAfterDecision();
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  /**
   * Takes the LocationVDrive id, same as verify, never the location id. Every
   * location on this vDrive balance that is still in verification is rejected,
   * all with the same reason.
   */
  async function handleRejectLocation(reason: string) {
    try {
      setLoading(true);
      const { data } = await rejectVDrive(vDriveVerification!.id, reason);
      SuccessToast(data.message);
      setRejectModal(false);
      refreshAfterDecision();
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  async function handleGetLocation() {
    try {
      setLoading(true);
      const { data } = await getLocationVerificationVDriveWithId(id);
      setVDriveVerification(data.data);

      setAutoAssignDriver(data?.data?.autoAssign || false);
      setEnableVgrubsBrands(data?.data?.vGrubsBrandsEnabled || false);
      setAchCustomerId(data?.data?.achCustomerId || '');
      setVDriveFee(data?.data?.vDriveFee || null);
      setVGrubsFee(data?.data?.vGrubsFee || null);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (id) {
      handleGetLocation();
    }
  }, []);

  const handleValueVDriveFee = useCallback((val) => {
    setVDriveFee(val);
  }, []);

  const handleValueVGrubsFee = useCallback((val) => {
    setVGrubsFee(val);
  }, []);

  const { location, owner } = vDriveVerification || {};
  const payment = vDriveVerification?.paymentMode
    ? PAYMENT_MODE[vDriveVerification.paymentMode]
    : null;

  return (
    <>
      <AdminHeader title={'vDrive Verification Details'}>
        <OpenConversationButton userId={owner?.id} role={owner?.role} />
      </AdminHeader>

      {loading && <AdminSpinner />}
      {vDriveVerification && (
        <>
          <Div
            $flex
            $columnSM
            $gap="32px"
            $gapSM="20px"
            $m="32px 0"
            $mSM="20px 0"
            $between
            $alignCenter
          >
            <Div $flex $alignCenter $gap="20px" $gapSM="16px">
              <VDriveLogo width="100" height="100" />
              <Div>
                <H3 $mb="8" $color={lightTheme.primary}>
                  {location?.name}
                </H3>
                <P $m="0">{location?.address}</P>
              </Div>
            </Div>

            <Div
              $flex
              $alignEnd
              $justifyCenter
              $column
              $gap="16px"
              $widthSM="100%"
            >
              {/* The backend answers 400 to a reject in any other status. */}
              {vDriveVerification.status === VDriveStatus.inProgress && (
                <Button
                  $selected
                  $gray
                  $w="250px"
                  $wSM="100%"
                  onClick={() => setRejectModal(true)}
                >
                  Reject vDrive
                </Button>
              )}
              <Button
                $selected
                $w="250px"
                $wSM="100%"
                onClick={handleVerifyLocation}
              >
                Enable vDrive
              </Button>
            </Div>
          </Div>

          <Div $flex $columnSM $gap="32px" $gapSM="24px">
            <Div $width="40%" $widthSM="100%" $flex $column $gap="20px">
              <P $m="0" $bold $color={lightTheme.lightGray}>
                Submitted by the restaurant
              </P>

              <SectionCard title="Contact">
                <KeyValueInfo
                  k="Owner"
                  v={`${owner?.firstName ?? ''} ${
                    owner?.lastName ?? ''
                  }`.trim()}
                />
                <KeyValueInfo
                  k="Owner phone"
                  v={owner?.phoneNumber}
                  component={
                    <PhoneNumberText phone={owner?.phoneNumber ?? ''} title />
                  }
                />
                <KeyValueInfo
                  k="Restaurant phone"
                  v={location?.phoneNumber || '/'}
                  component={
                    location?.phoneNumber ? (
                      <PhoneNumberText phone={location.phoneNumber} title />
                    ) : undefined
                  }
                  last
                />
              </SectionCard>

              {payment && (
                <SectionCard title="Payment">
                  <KeyValueInfo
                    k="How they pay"
                    v={payment.label}
                    component={
                      <>
                        <H5 $mb="4">{payment.label}</H5>
                        <Disclaimer $m="0" $color={lightTheme.lightGray}>
                          {payment.note}
                        </Disclaimer>
                      </>
                    }
                    last
                  />
                </SectionCard>
              )}
            </Div>

            <Div $width="60%" $widthSM="100%" $flex $column $gap="20px">
              <P $m="0" $bold $color={lightTheme.lightGray}>
                Set up before enabling
              </P>

              <SectionCard title="Fees per delivery">
                <Div $flex $gap="20px" $gapSM="12px">
                  <Div $width="50%">
                    <AdminDollarInput2
                      label="vDrive Fee"
                      onValueChange={handleValueVDriveFee}
                      value={vDriveFee}
                      $textCenter
                    />
                  </Div>
                  <Div $width="50%">
                    <AdminDollarInput2
                      label="vGrubs Fee"
                      onValueChange={handleValueVGrubsFee}
                      value={vGrubsFee}
                      $textCenter
                    />
                  </Div>
                </Div>
                <Disclaimer $m="0" $mt="8" $color={lightTheme.lightGray}>
                  vDrive Fee at least $0.80, vGrubs Fee at least $0.20.
                </Disclaimer>
              </SectionCard>

              <Box $p="20px 24px" $pSM="16px">
                <AdminInput
                  tooltip={ACH_CUSTOMER_ID_TOOLTIP}
                  $textCenter
                  label="ACH Customer ID"
                  placeholder="Enter here"
                  value={achCustomerId}
                  onChange={(e) => setAchCustomerId(e.target.value)}
                />
              </Box>

              <SectionCard title="Options">
                <Div $flex $alignCenter $between $gap="16px" $p="8px 0">
                  <H5 $m="0">Enable vDrive for vGrubs Brands</H5>
                  <AdminSwitch
                    checked={enableVgrubsBrands}
                    onChange={() => setEnableVgrubsBrands((prev) => !prev)}
                  />
                </Div>
                <Div $flex $alignCenter $between $gap="16px" $p="8px 0">
                  <Div>
                    <H5 $mb="4">Automatically Assign Driver</H5>
                    <Disclaimer $m="0" $color={lightTheme.lightGray}>
                      Based on cheapest and fastest routing.
                    </Disclaimer>
                  </Div>
                  <AdminSwitch
                    checked={autoAssignDriver}
                    onChange={() => setAutoAssignDriver((prev) => !prev)}
                  />
                </Div>
              </SectionCard>
            </Div>
          </Div>
        </>
      )}

      <RejectVDriveModal
        isOpen={rejectModal}
        closeModal={() => setRejectModal(false)}
        onConfirm={handleRejectLocation}
        loading={loading}
        locationName={location?.name}
      />
    </>
  );
};
